import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Input, Button, Space, Divider, Tag, Checkbox, message, Upload, Tabs } from 'antd';
import { SendOutlined, PaperClipOutlined, MailOutlined } from '@ant-design/icons';
import api from '../services/api';
import EditorEmail from './EditorEmail';

const { Option } = Select;
const { TextArea } = Input;

const VARIAVEIS = [
  { label: 'Nome cliente', valor: '{{cliente.nome}}' },
  { label: 'Nº proposta', valor: '{{proposta.numero}}' },
  { label: 'Valor proposta', valor: '{{proposta.valor}}' },
  { label: 'Vencimento boleto', valor: '{{boleto.vencimento}}' },
  { label: 'Linha digitável', valor: '{{boleto.linha_digitavel}}' },
  { label: 'Nº NFS-e', valor: '{{nfse.numero}}' },
  { label: 'Data NFS-e', valor: '{{nfse.data_emissao}}' },
  { label: 'Valor NFS-e', valor: '{{nfse.valor}}' },
  { label: 'Usuário', valor: '{{usuario.nome}}' },
  { label: 'Data hoje', valor: '{{data_hoje}}' },
];

const ANEXOS_OPCOES = [
  { label: 'Boleto PDF', value: 'boleto_pdf', tipo: 'boleto' },
  { label: 'NFS-e PDF', value: 'nfse_pdf', tipo: 'nfse' },
  { label: 'NFS-e XML', value: 'nfse_xml', tipo: 'nfse' },
  { label: 'Proposta PDF', value: 'proposta_pdf', tipo: 'proposta' },
];

function ModalComunicacao({ propostaId, open, onClose, boletoId, tipoProposta }) {
  const [templates, setTemplates] = useState([]);
  const [dados, setDados] = useState(null);
  const [templateSelecionado, setTemplateSelecionado] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (open && propostaId) {
      carregarDados();
      carregarTemplates();
    }
  }, [open, propostaId, boletoId]);

  const carregarTemplates = async () => {
    try {
      const r = await api.get('/comunicacao/templates');
      setTemplates(r.data || []);
    } catch {}
  };

  const carregarDados = async () => {
    try {
      const r = await api.get('/comunicacao/proposta/' + propostaId + '/dados', { params: { boleto_id: boletoId || undefined, tipo: tipoProposta || undefined } });
      setDados(r.data);
      // Pre-selecionar primeiro email disponivel
      if (r.data.emails?.length > 0) {
        form.setFieldsValue({ destinatarios: [r.data.emails[0].email] });
      }
    } catch {}
  };

  const selecionarTemplate = (templateId) => {
    const t = templates.find(t => t.id === templateId);
    if (!t) return;
    setTemplateSelecionado(t);
    form.setFieldsValue({
      remetente: t.remetente,
      assunto: substituirVariaveis(t.assunto),
      corpo: t.corpo,
      anexos: t.anexos_padrao || [],
    });
  };

  const usuarioLogado = JSON.parse(localStorage.getItem('usuario') || '{}');

  const substituirVariaveis = (texto) => {
    if (!texto) return texto;
    const d = dados || {};
    return String(texto).replace(/\{\{([^}]*)\}\}/g, (m, k) => '{{' + k.replace(/<[^>]*>/g, '').replace(/&nbsp;|\u00a0|\u200b/g, '').replace(/\s+/g, '') + '}}')
      .replace(/{{cliente\.nome}}/g, d.cliente?.nome || d.cliente?.razao_social || '')
      .replace(/{{cliente\.cnpj}}/g, d.cliente?.cnpj || '')
      .replace(/{{proposta\.numero}}/g, dados.proposta?.numero || '')
      .replace(/{{proposta\.valor}}/g, dados.proposta?.valor || '')
      .replace(/{{proposta\.titulo}}/g, dados.proposta?.titulo || '')
      .replace(/{{proposta\.validade}}/g, dados.proposta?.validade || '')
      .replace(/{{boleto\.vencimento}}/g, dados.boleto?.vencimento || '')
      .replace(/{{boleto\.valor}}/g, dados.boleto?.valor || '')
      .replace(/{{boleto\.linha_digitavel}}/g, dados.boleto?.linha_digitavel || '')
      .replace(/{{nfse\.numero}}/g, dados.nfse?.numero || '')
      .replace(/{{nfse\.data_emissao}}/g, dados.nfse?.data_emissao || '')
      .replace(/{{nfse\.valor}}/g, dados.nfse?.valor || '')
      .replace(/{{data_hoje}}/g, new Date().toLocaleDateString('pt-BR'))
      .replace(/{{usuario\.nome}}/g, usuarioLogado.nome || 'Equipe Celum');
  };

  const inserirVariavel = (variavel) => {
    const campo = form.getFieldValue('corpo') || '';
    form.setFieldsValue({ corpo: campo + variavel });
  };

  const enviar = async (values) => {
    try {
      setEnviando(true);
      // Montar anexos com IDs
      const anexosComId = (values.anexos || []).map(tipo => {
        const obj = { tipo };
        if (tipo.includes('boleto') && dados?.boleto) obj.id = dados.boleto.id;
        if (tipo.includes('nfse') && dados?.nfse) obj.id = dados.nfse.id;
        return obj;
      });

      // Substituir variaveis no corpo
      const corpoFinal = substituirVariaveis(values.corpo);

      await api.post('/comunicacao/proposta/' + propostaId + '/enviar', {
        template_id: values.template_id,
        remetente: values.remetente,
        destinatarios: values.destinatarios,
        assunto: substituirVariaveis(values.assunto),
        corpo: corpoFinal,
        anexos: anexosComId,
        tipo_proposta: tipoProposta || undefined,
      });

      message.success('E-mail enviado com sucesso!');
      onClose();
      form.resetFields();
    } catch(e) {
      message.error('Erro ao enviar: ' + (e.response?.data?.erro || e.message));
    } finally { setEnviando(false); }
  };

  return (
    <Modal title={<><MailOutlined /> Comunicação — Proposta {dados?.proposta?.numero}{boletoId && dados?.boleto ? ' · Boleto venc. ' + dados.boleto.vencimento : ''}</>}
      open={open} onCancel={onClose} footer={null} width={750} destroyOnClose>
      <Form form={form} layout="vertical" onFinish={enviar}>

        {/* Template */}
        <Form.Item name="template_id" label="Template">
          <Select placeholder="Selecione um template" onChange={selecionarTemplate} allowClear>
            {templates.map(t => (
              <Option key={t.id} value={t.id}>
                <Tag color={t.remetente === 'comercial' ? 'blue' : 'green'} style={{ marginRight: 6 }}>
                  {t.remetente}
                </Tag>
                {t.nome}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <div style={{ display: 'flex', gap: 12 }}>
          {/* Remetente */}
          <Form.Item name="remetente" label="Remetente" style={{ flex: 1 }} rules={[{ required: true }]}>
            <Select>
              <Option value="financeiro">financeiro@celum.com.br</Option>
              <Option value="comercial">comercial@celum.com.br</Option>
            </Select>
          </Form.Item>

          {/* Destinatários */}
          <Form.Item name="destinatarios" label="Destinatários" style={{ flex: 2 }} rules={[{ required: true }]}>
            <Select mode="tags" placeholder="Selecione ou digite e-mails">
              {(dados?.emails || []).map(e => (
                <Option key={e.email} value={e.email}>{e.label} — {e.email}</Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        {/* Assunto */}
        <Form.Item name="assunto" label="Assunto" rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        {/* Anexos */}
        <Form.Item name="anexos" label="Anexos">
          <Select mode="multiple" placeholder="Selecione os anexos">
            {ANEXOS_OPCOES.map(a => {
              const disponivel = (a.tipo === 'boleto' && dados?.boleto) ||
                                 (a.tipo === 'nfse' && dados?.nfse) ||
                                 a.tipo === 'proposta';
              return (
                <Option key={a.value} value={a.value} disabled={!disponivel}>
                  {a.label} {!disponivel && '(não disponível)'}
                </Option>
              );
            })}
          </Select>
        </Form.Item>

        {/* Editor visual */}
        <Form.Item name="corpo" label="Mensagem" rules={[{ required: true }]}>
          <EditorEmail
            value={form.getFieldValue('corpo')}
            onChange={val => form.setFieldValue('corpo', val)}
          />
        </Form.Item>

        <div style={{ background: '#e6f7ff', padding: 8, borderRadius: 6, marginBottom: 12, fontSize: 12 }}>
          📧 Cópia automática enviada para: <strong>contato@celum.com.br</strong>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="primary" htmlType="submit" loading={enviando} icon={<SendOutlined />}>
            Enviar E-mail
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

export default ModalComunicacao;
